import {
  AdaCodeArray,
  CorrelationEnum,
  SponsorTypeArray,
  ToothArray,
  QuadrantArray,
  ArchArray, 
  RelationToSubscriberEnum,
  TicketStatusEnum,
  TQuadrant,
  TTooth,
  TSurface,
  SurfacePostteriorArray,
  SurfaceAnteriorArray,
  AnteriorTeeth,
  PosterioiTeeth,
  TTimePeriodArray
} from '../interfaces/interfaces.v1';
import * as yup from 'yup';
import invariant from 'invariant';
import _, { isArray, mapValues } from 'lodash';

function atLeastOneRequired(this: any, list: any, message: any) {
  invariant(list.every((field: any) => this.fields[field]), 'All required fields should be defined before calling atLeastOneRequired');
  return this.shape(list.reduce((acc: any, field: any) => ({
    ...acc,
    [field]: this.fields[field].when(_.without(list, field), {
      is: (...values: any[]) => !values.some(item => item),
      then: this.fields[field].required(message),
    }),
  }), {}), list.reduce((acc: any, item: any, idx: any, all: any) => [...acc, ...all.slice(idx + 1).map((i: any) => [item, i])], []));
}

yup.addMethod(yup.object, 'atLeastOneRequired', function(list) {
  return this.test({
    name: 'atLeastOneOf',
    message: '${path} must have at least one of these keys: ${keys}',
    exclusive: true,
    params: { keys: list.join(', ') },
    test: value => value == null || list.some((f: string) => value[f] != null)
  })
});
yup.addMethod(yup.string, 'integer', function (value) {
  return this.matches(/^\d+$/, `${this.__inputType} The field should have digits only`);
})

export class ValidatorV1 {
  private static instance: ValidatorV1;
  public static getInstance() {
    if (!ValidatorV1.instance) {
      ValidatorV1.instance = new ValidatorV1();
    }

    return ValidatorV1.instance;
  }

  constructor() {
    AdaCodeArray.forEach(code => {
      if (!this.objForAdaShapes[code] && code !== 'NONE') {
        this.objForAdaShapes[code] = this.VAdaObject(true).default(undefined);
      }
    });
    AnteriorTeeth.forEach(teeth => {
      if(!this.objForSurfaces[teeth]){
        this.objForSurfaces[teeth] =  yup.string().matches(/^(?!.*?(.).*?\1)[MDLIF]+$/);
      }
    });
    PosterioiTeeth.forEach(teeth => {
      if(!this.objForSurfaces[teeth]){
        this.objForSurfaces[teeth] = yup.string().matches(/^(?!.*?(.).*?\1)[MDLOB]+$/);
      }
    })
  }

  public objForAdaShapes: { [key: string]: any} = {};
  public objForSurfaces: { [key: TTooth]: any } = {};
  
  VFieldHistory = yup.object({
    userHash: yup.string().required(),
    timestamp: yup.number().required(),
    value: yup.mixed()
  }).noUnknown();

  VLimitation = yup.object({
    quantity: yup.number().positive().required(),
    frequency: yup.number().positive().required(),
    timePeriod: yup.string().oneOf([...TTimePeriodArray]).required()
  });

  VAge = yup.object({
    min: yup.number().min(0).max(359).nullable(),
    max: yup.number().max(360).nullable().when(
      'min',
      (min, schema) => (min && !isNaN(min) ? schema.min(min) : schema.min(0)),
    ),
  });

  VWaitingPeriod = (yup.object({
    months: yup.number().max(9999).min(0).nullable(true).default(undefined),
    days: yup.number().max(36000).min(0).nullable(true).default(undefined),
    weeks: yup.number().max(5400).min(0).nullable(true).default(undefined),
    years: yup.number().max(9999).min(0).nullable(true).default(undefined)
  }) as any).atLeastOneRequired(['months','days','years', 'weeks']);

  VCorrelatedWithData = yup.object({
    value:  yup.lazy(val => Array.isArray(val) ? 
      yup.array().of(yup.string()) : this.VWaitingPeriod.default(undefined))
  });

  VScoredValueString = yup.object({
    value: yup.string(),
    score: yup.number().integer().positive().min(0).max(3),
    changeHistory: yup.array().of(this.VFieldHistory)
  });

  VScoredValueNumber = yup.object({
    value: yup.number(),
    score: yup.number().integer().positive().min(0).max(3),
    changeHistory: yup.array().of(this.VFieldHistory)
  });

  VScoredValueBoolean = yup.object({
    value: yup.boolean(),
    score: yup.number().integer().positive().min(0).max(3),
    changeHistory: yup.array().of(this.VFieldHistory)
  });

  VScoredValueLimitation = yup.object({
    value: this.VLimitation,
    score: yup.number().integer().positive().min(0).max(3),
    changeHistory: yup.array().of(this.VFieldHistory)
  });

  VScoredValueAge = yup.object({
    value: this.VAge,
    score: yup.number().integer().positive().min(0).max(3),
    changeHistory: yup.array().of(this.VFieldHistory)
  });

  VScoredValueWaitingPeriod = yup.object({
    value: this.VWaitingPeriod,
    score: yup.number().integer().positive().min(0).max(3),
    changeHistory: yup.array().of(this.VFieldHistory)
  });

  VScoredArrayOrString = yup.object({
    value: yup.mixed(),
    score: yup.number().integer().positive().min(0).max(3),
    changeHistory: yup.array().of(this.VFieldHistory)
  });

  VScoredCorrelatedWithData = yup.object({
    value: yup.lazy(val => Array.isArray(val) ? 
    yup.array().of(yup.string()) : this.VWaitingPeriod.default(undefined)),
    score: yup.number().integer().positive().min(0).max(3),
    changeHistory: yup.array().of(this.VFieldHistory)
  });

  VScoredOrNot = (type: 'string' | 'number' | 'boolean' | 'limitation' | 'age' | 'waitingPeriod' | 'arrayOrString' | 'correlatedWithData', scored?: boolean) => {
    switch (type) {
      case 'string':
        return scored ? this.VScoredValueString : yup.string();
      case 'number':
        return scored ? this.VScoredValueNumber : yup.number();
      case 'boolean':
        return scored ? this.VScoredValueBoolean : yup.boolean();
      case 'limitation':
        return scored ? this.VScoredValueLimitation : this.VLimitation;
      case 'age':
        return scored ? this.VScoredValueAge : this.VAge;
      case 'waitingPeriod':
        return scored ? this.VScoredValueWaitingPeriod : this.VWaitingPeriod;
      case 'arrayOrString':
        return scored ? this.VScoredArrayOrString : yup.mixed();
      case 'correlatedWithData':
        return scored ? this.VScoredCorrelatedWithData : this.VCorrelatedWithData;
    }
  }

  VAttachmets(scored?: boolean) {
    return yup.array().of(yup.string().url());
  }

  VSponsor (scored?: boolean){
    return yup.object({
      sponsorName: yup.string(),
      sponsorType: yup.string().oneOf([...SponsorTypeArray]),
      groupName: yup.string(),
      groupNumber: yup.string()
    });
  } 

  VPayer (scored?: boolean) {
    return yup.object({
      id: yup.string(),
      name: yup.string(),
      street1: yup.string(),
      street2: yup.string(),
      city: yup.string(),
      state: yup.string(),
      zipCode: yup.string(),
      phoneNumber: yup.string(),
    });
  }

  VHistoryRecord(scored?: boolean) {
    return yup.object({
      code: yup.string().oneOf([...AdaCodeArray]),
      date: yup.number().required(),
      notes: yup.string(),
      tooth: yup.array().of(yup.string().oneOf(ToothArray)),
      surfaces: yup.array().of(yup.object().shape(this.objForSurfaces)),
      quadrants: yup.array().of(yup.mixed<TQuadrant>().oneOf(QuadrantArray)),
      arch: yup.string().oneOf(ArchArray),
      toothRange: yup.array().of(yup.mixed<TTooth>().oneOf(ToothArray)),
    }); 
  }

  VPatient(scored?: boolean) {
    return yup.object({
      deductibleRemaining: yup.number(),
      insuranceRemaining: yup.number(),
      effectiveDate: yup.number(),
      note: yup.string(),
      history: yup.array().of(this.VHistoryRecord(scored))
    });
  }

  VAdaObjectCustomExtended(scored?: boolean) {
    return yup.object().shape({
      appliesToAnnualMax: this.VScoredOrNot('boolean', scored),
      coinsurance: this.VScoredOrNot('number', scored),
      deductible: this.VScoredOrNot('number', scored),
      waitingPeriod: this.VScoredOrNot('waitingPeriod', scored).default(undefined),
      lifetimeMaximum: this.VScoredOrNot('number', scored),
      limitation: this.VScoredOrNot('limitation', scored).default(undefined),
      caveat: yup.array().of(yup.string()),
      correlatedWith: yup.array().of(yup.object({
        code: yup.mixed().oneOf(AdaCodeArray as any),
        how: yup.mixed().oneOf(Object.values(CorrelationEnum)),
        apply: this.VScoredOrNot('boolean', scored),
        data: this.VScoredOrNot('correlatedWithData', scored).default(undefined),
      })),
      age: this.VScoredOrNot('age', scored),
    });
  }

  VAdaObject(scored?: boolean) {
    return yup.object().shape({
      code: yup.mixed().oneOf(AdaCodeArray as any).required(),
      category: yup.string().required(),
      wholeCategory: this.VScoredOrNot('boolean', scored),
      appliesToAnnualMax: this.VScoredOrNot('boolean', scored),
      coinsurance: this.VScoredOrNot('number', scored),
      deductible: this.VScoredOrNot('number', scored),
      waitingPeriod: this.VScoredOrNot('waitingPeriod', scored).default(undefined),
      lifetimeMaximum: this.VScoredOrNot('number', scored),
      limitation: this.VScoredOrNot('limitation', scored).default(undefined),
      caveat: yup.array().of(yup.string()),
      correlatedWith: yup.array().of(yup.object({
        code: yup.mixed().oneOf(AdaCodeArray as any),
        how: yup.mixed().oneOf(Object.values(CorrelationEnum)),
        apply: this.VScoredOrNot('boolean', scored),
        data: this.VScoredOrNot('correlatedWithData', scored).default(undefined),
      })),
      exceptions: yup.object({
        child: this.VAdaObjectCustomExtended(scored),
        dependent: this.VAdaObjectCustomExtended(scored)
      }).optional(),
      age: this.VScoredOrNot('age', scored),
    });
  } 

  VBreakdown(scored?: boolean) {
    return yup.object({
      missingToothClause: this.VScoredOrNot('boolean', scored),
      annualMax: this.VScoredOrNot('number', scored),
      deductible: yup.object({
        individual: this.VScoredOrNot('number', scored),
        family: this.VScoredOrNot('number', scored),
      }),
      adaCodes:  yup.object().shape(this.objForAdaShapes),
    });
  } 

  VDetails(scored?: boolean) {
    return yup.object({
      benefitYear: this.VScoredOrNot('string', scored),
      participation: yup.string(),
      initiationDate: yup.number().nullable(true),
    });
  }

  VPlan (scored?: boolean) {
    return yup.object({
      benefits: this.VBreakdown(scored).required(),
      payer: this.VPayer(scored).required(),
      sponsor: this.VSponsor(scored).required(),
      score: yup.number().nullable(true),
      details: this.VDetails(scored).required(),
      userHashes: yup.array().of(yup.string()),
      planHash: yup.string(),
      lastSubmition: yup.number(),
      schemaVersion: yup.string(),
    });
  }

  VAlertNotes(scored?: boolean) {
    return yup.object({
      creationDate: yup.number().required(),
      alertContent: yup.string().required(),
      user: yup.string().required()
    });
  }

  VClientInfo =  yup.object({
    name: yup.string(),
    primaryDoctorName: yup.string(),
    taxId: yup.string(),
    phone: yup.string(),
    npi: yup.string(),
    address: yup.string(),
    maleAgent: yup.string(),
    femaleAgent: yup.string()
  });

  VPatientInfo =  yup.object({
    patientFirstName: yup.string().required(),
    patientLastName: yup.string().required(),
    patientDOB: yup.string().required(),
    patientMemberId: yup.string().required(),
    patientAddress: yup.string(),
    patientRelationToSubscriber: yup.mixed().oneOf(Object.values(RelationToSubscriberEnum)).required(),
    subscriberFirstName: yup.string(),
    subscriberLastName: yup.string(),
    subscriberDOB: yup.string(),
    subscriberMemberId: yup.string()
  });

  VTicket(scored?: boolean) {
    return yup.object({
      integrationPatientInfo: this.VPatientInfo.default(undefined),
      integrationClientInfo: this.VClientInfo.default(undefined),
      resolvedDate: yup.number(),
      draft: this.VPlan(scored).default(undefined),
      submitPlanId: yup.string(),
      status: yup.mixed().oneOf(Object.values(TicketStatusEnum)),
      verrificPlusInteralId: yup.string().required(),
      clientSpecificPlanId: yup.string(),
      payerAgentName: yup.string(),
      callReference: yup.string(),
      alertNotes: yup.array().of(this.VAlertNotes(scored)),
      documents: this.VAttachmets(scored),
      externalUrl: yup.string(),
      integration: yup.string(),
      externalId: yup.string(),
      ticketHash: yup.string(),
      schemaVersion: yup.string(),
      isDirty: yup.boolean(),
      requesterId: yup.string(),
      imported: yup.boolean(),
      ehr: yup.string(),
      ehrVersion: yup.string(),
      pluginName: yup.string(),
      pluginVersion: yup.string(),
      pluginEnvironment: yup.string()
    });
  }

  VTicketWrapper(scored?: boolean){
    return  yup.object({
      patient: this.VPatient(scored).required(),
      plan: this.VPlan(scored).default(undefined).nullable(true),
      ticket: this.VTicket(scored).default(undefined),
      schemaVersion: yup.string()
    });
  }
}